// ---- Quest tracker + waypoint marker (client-only) ----
// Presentational HUD over the local player's active quest:
//   1. Top-center objective tracker ("Talk to Dekkar on the dock")
//   2. A gold ground ring + overhead diamond over the target NPC (the active
//      waypoint) — never networked, never visible to other clients
//
// State lives in quest.ts (`currentWaypoint`); this module is pure view. Refresh
// the tracker text whenever quest progress changes (`refreshQuestHud`); project
// the world marker every frame from the game loop (`updateQuestHud`).

import * as THREE from "three";
import { game } from "./state.ts";
import { currentWaypoint } from "./quest.ts";
import { liveById } from "./npc/index.ts";

const WAYPOINT_COLOR = 0xffd86a; // warm gold — distinct from focus combat/peace rings

let trackEl: HTMLElement | null = null;
let markerEl: HTMLElement | null = null;
let ring: THREE.Group | null = null;
let camera: THREE.Camera | null = null;
const _v = new THREE.Vector3();

/** Wire the tracker DOM + the client-only ground ring. Call once at boot. */
export function initQuestHud(scene: THREE.Scene, cam: THREE.Camera): void {
  camera = cam;
  trackEl = document.getElementById("questtrack");
  markerEl = document.getElementById("questmarker");
  if (!ring) {
    ring = makeWaypointRing();
    ring.visible = false;
    scene.add(ring);
  }
  refreshQuestHud();
}

/** Re-sync the top-center tracker text from quest progress. Call after
 *  `setQuests` (and anytime the active step may have changed). */
export function refreshQuestHud(): void {
  if (!trackEl) return;
  const wp = currentWaypoint();
  if (!wp || !game.joined) {
    trackEl.style.display = "none";
    trackEl.textContent = "";
    return;
  }
  trackEl.style.display = "block";
  // Title above, objective below — keeps multi-quest readiness cheap (still one
  // tracker for now; title disambiguates when we later pick among several).
  trackEl.innerHTML =
    `<span class="qtitle">${escapeHtml(wp.questTitle)}</span>` +
    `<span class="qobj">${escapeHtml(wp.desc)}</span>`;
}

/** Per-frame: project the overhead diamond + seat the ground ring on the active
 *  waypoint NPC (if any and in interest). No-op when no waypoint / no target. */
export function updateQuestHud(time: number): void {
  if (!camera || !ring || !markerEl) return;
  const wp = game.joined ? currentWaypoint() : null;
  const L = wp?.targetNpcId ? liveById(wp.targetNpcId) : undefined;
  // Only mark a living, interest-visible NPC. Defeated/hidden targets drop the
  // marker; the top tracker still names the objective so the player isn't lost.
  const show = !!(L && !L.defeated && !L.netHidden && L.group.visible);
  if (!show) {
    ring.visible = false;
    markerEl.style.display = "none";
    return;
  }

  const p = L!.group.position;
  // Ground ring — pulse + slow spin so it reads as "active waypoint" (the focus
  // ring's twin, gold instead of combat/peace colors).
  ring.visible = true;
  ring.position.set(p.x, L!.feetY + 0.05, p.z);
  const pulse = 1 + Math.sin(time * 3) * 0.08;
  ring.scale.set(pulse, pulse, pulse);
  ring.rotation.y = time * 0.5;

  // Overhead diamond — slightly above the nameplate so the two don't stack.
  _v.set(p.x, L!.feetY + L!.height + 0.85, p.z).project(camera);
  if (_v.z > 1) {
    markerEl.style.display = "none";
    return;
  }
  const W = window.innerWidth, H = window.innerHeight;
  markerEl.style.display = "block";
  markerEl.style.left = `${(_v.x * 0.5 + 0.5) * W}px`;
  markerEl.style.top = `${(-_v.y * 0.5 + 0.5) * H}px`;
}

/** Flat gold ring, unlit, client-local — never rides the wire. */
export function makeWaypointRing(): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({
    color: WAYPOINT_COLOR,
    transparent: true,
    opacity: 0.8,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const outer = new THREE.Mesh(new THREE.RingGeometry(0.95, 1.2, 40), mat);
  outer.rotation.x = -Math.PI / 2;
  const inner = new THREE.Mesh(new THREE.RingGeometry(0.3, 0.45, 24), mat.clone());
  inner.rotation.x = -Math.PI / 2;
  g.add(outer, inner);
  g.renderOrder = 10;
  return g;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
